import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Fab,
  Avatar,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import { api } from '../../utils/api';
import { useClient } from '../../contexts/ClientContext';
import MarkdownRenderer from '../common/MarkdownRenderer';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const Chatbot: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hi! I'm your Search Intelligence assistant. I can help you understand your AEO and SEO performance data. Ask me anything about your metrics, citations, rankings, or trends!",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { selectedClient } = useClient();
  const clientName = selectedClient?.clientName || '';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Reset conversation when user switches clients so context and answers match the current client
  useEffect(() => {
    setMessages([
      {
        id: '1',
        text: "Hi! I'm your Search Intelligence assistant. I can help you understand your AEO and SEO performance data. Ask me anything about your metrics, citations, rankings, or trends!",
        isUser: false,
        timestamp: new Date()
      }
    ]);
  }, [clientName]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || !clientName || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input.trim(),
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/api/chatbot/ask', {
        question: userMessage.text,
        clientName: clientName
      });

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response.answer || 'I apologize, but I was unable to generate a response.',
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error: any) {
      // Extract error message with better handling
      let errorText = 'Sorry, I encountered an error. Please try again.';
      
      if (error?.response?.data?.answer) {
        // Backend provides user-friendly error message
        errorText = error.response.data.answer;
      } else if (error?.response?.status === 429) {
        // Rate limit error
        errorText = 'Too many requests. Please wait a moment and try again.';
      } else if (error?.response?.status === 500) {
        // Server error
        if (error?.response?.data?.error?.includes('timeout')) {
          errorText = 'The request took too long. Please try again with a simpler question.';
        } else if (error?.response?.data?.error?.includes('credentials')) {
          errorText = 'The AI service is not properly configured. Please contact support.';
        } else {
          errorText = 'Server error. Please try again later.';
        }
      } else if (error?.message) {
        // Network or other errors
        if (error.message.includes('timeout') || error.message.includes('Network')) {
          errorText = 'Connection timeout. Please check your internet connection and try again.';
        } else {
          errorText = error.message;
        }
      }
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: errorText,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const suggestedQuestions = [
    "What's my current performance score?",
    "How many citations do I have?",
    "What are my top performing keywords?",
    "Show me recent trends",
    "What should I focus on to improve?"
  ];
  const visibleSuggestedQuestions = suggestedQuestions.slice(0, 3);

  const handleSuggestedQuestion = (question: string) => {
    setInput(question);
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          boxShadow: 3,
        }}
        onClick={() => setOpen(prev => !prev)}
      >
        <ChatIcon />
      </Fab>

      {/* Chat Panel */}
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 88, sm: 88 },
            right: { xs: 16, sm: 24 },
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            height: { xs: '70vh', sm: 520 },
            maxHeight: '70vh',
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5
          }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SmartToyIcon />
            <Typography variant="h6" fontWeight={600}>
              Search Intelligence Assistant
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
          </Box>
        
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden'
        }}>
          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: 'grey.50',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                  gap: 1,
                  alignItems: 'flex-start'
                }}
              >
                {!message.isUser && (
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                    <SmartToyIcon fontSize="small" />
                  </Avatar>
                )}
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    maxWidth: '75%',
                    bgcolor: message.isUser ? 'primary.main' : 'background.paper',
                    color: message.isUser ? 'primary.contrastText' : 'text.primary',
                    borderRadius: 2,
                    borderTopLeftRadius: message.isUser ? 2 : 0,
                    borderTopRightRadius: message.isUser ? 0 : 2,
                  }}
                >
                  {message.isUser ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {message.text}
                    </Typography>
                  ) : (
                    <Box sx={{ '& p': { margin: 0 }, '& p + p': { mt: 1 }, '& strong': { fontWeight: 600 } }}>
                      <MarkdownRenderer content={message.text} />
                    </Box>
                  )}
                </Paper>
                {message.isUser && (
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                )}
              </Box>
            ))}
            
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, alignItems: 'flex-start' }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                  <SmartToyIcon fontSize="small" />
                </Avatar>
                <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2 }}>
                  <CircularProgress size={16} />
                </Paper>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Suggested Questions */}
          {messages.length === 1 && (
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Suggested questions:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {visibleSuggestedQuestions.map((q, idx) => (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      p: 0.75,
                      px: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'grey.200'
                      }
                    }}
                    onClick={() => handleSuggestedQuestion(q)}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {q}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {/* Input Area */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end'
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={clientName ? "Ask me anything about your data..." : "Select a client first"}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={!clientName || loading}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <Tooltip title="Send message">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!input.trim() || !clientName || loading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled'
                    }
                  }}
                >
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        </Paper>
      )}
    </>
  );
};

export default Chatbot;
